import { get, send } from "./api.js";
export { MAX_GROOVE_TITLE_LENGTH } from "@wmf/shared/teachingAssetLimits";

/**
 * Grooves, from the browser's side.
 *
 * The package ships a `library.ts` that keeps grooves in `localStorage`. This
 * replaces it: a groove kept in one browser is invisible to the tutor who set
 * it and gone when a child uses a different device, which is the whole reason
 * the portal is storing them at all.
 */

export interface Groove {
  id: string;
  title: string;
  /** The canonical query string. The groove itself — see `@wmf/groove`. */
  query: string;
  authorUserId: string | null;
  originGrooveId: string | null;
  createdAt: string;
  updatedAt: string;
  /** What a save must send back, so a stale one is refused rather than merged. */
  version: number;
}

/** A groove somebody was given, with who set it and what they said. */
export interface SharedGroove {
  shareId: string;
  studentId: string;
  studentName: string;
  note: string | null;
  sharedAt: string;
  /** The tutor's name, or null once their account has gone. */
  sharedBy: string | null;
  /** Whether it was also set as work before the next lesson. */
  setAsPractice: boolean;
  practiceDone: boolean;
  /**
   * The groove **as it was shared**, and where it plays.
   *
   * `groove.title` and `groove.query` are the share's snapshot, so a groove
   * edited since still reads here as what was actually given. `token` is the
   * durable address: it survives the groove being deleted, which
   * `/grooves/<id>` does not.
   */
  groove: Groove;
  token: string;
}

/**
 * Where a groove snapshot opens: the durable `/g/<token>` address a share,
 * practice task or lesson attachment carries. One helper, so the route and its
 * encoding are decided in one place.
 */
export function grooveSnapshotHref(token: string): string {
  return `/g/${encodeURIComponent(token)}`;
}

/** One entry in the roll of who has a groove. */
export interface GrooveShare {
  id: string;
  studentId: string;
  studentName: string;
  note: string | null;
  sharedAt: string;
  sharedByUserId: string | null;
  revokedAt: string | null;
}

export interface ShareableStudent {
  id: string;
  name: string;
  instrument: string | null;
}

export async function listGrooves(options: { q?: string; cursor?: string; includeShared?: boolean; sort?: "recent" | "title" } = {}): Promise<{
  grooves: Groove[];
  shared: SharedGroove[];
  /** Pass back as `cursor` for the next page. Null when this is the last one. */
  nextCursor: string | null;
  canCreate: boolean;
}> {
  const query = new URLSearchParams();
  if (options.q) query.set("q", options.q);
  if (options.cursor) query.set("cursor", options.cursor);
  if (options.sort) query.set("sort", options.sort);
  if (options.includeShared === false) query.set("includeShared", "false");
  const suffix = query.size === 0 ? "" : `?${query.toString()}`;
  return get(`/grooves${suffix}`);
}

/** Grooves shared with one student — the section on their record. */
export async function groovesForStudent(studentId: string): Promise<{ shared: SharedGroove[] }> {
  return get(`/grooves/for-student/${studentId}`);
}

/** Who this person may share with. An owner gets everybody; a tutor, their own. */
export async function shareableStudents(): Promise<{ students: ShareableStudent[] }> {
  return get("/grooves/shareable-students");
}

export async function listShares(grooveId: string): Promise<{ shares: GrooveShare[] }> {
  return get(`/grooves/${grooveId}/shares`);
}

export async function shareGroove(
  grooveId: string,
  studentId: string,
  note?: string,
  setAsPractice = false,
): Promise<{
  share: { id: string } | null;
  alreadyShared: boolean;
  practiceTodoId?: string | null;
}> {
  return send("POST", `/grooves/${grooveId}/shares`, {
    studentId,
    note: note ?? null,
    setAsPractice,
  });
}

/** Withdraw a share. Marked, never deleted — the row stays. */
export async function revokeShare(grooveId: string, shareId: string): Promise<void> {
  await send("DELETE", `/grooves/${grooveId}/shares/${shareId}`);
}

export async function listOrphanedGrooves(): Promise<{ grooves: Groove[] }> {
  return get("/grooves/orphaned");
}

export async function readGroove(
  id: string,
): Promise<{ groove: Groove; canEdit: boolean; canShare: boolean }> {
  return get(`/grooves/${id}`);
}

export async function createGroove(title: string, query: string): Promise<{ groove: Groove }> {
  return send("POST", "/grooves", { title, query });
}

/**
 * Save a change.
 *
 * `version` is what the editor opened, not what it is writing. A save from a
 * version that has moved on comes back as an `ApiError` with code `stale`,
 * carrying what is actually stored — which the page shows rather than
 * discarding what the person typed.
 */
export async function saveGroove(
  id: string,
  title: string,
  query: string,
  version: number,
): Promise<{ groove: Groove }> {
  return send("PUT", `/grooves/${id}`, { title, query, version });
}

export async function copyGroove(id: string, title?: string): Promise<{ groove: Groove }> {
  return send("POST", `/grooves/${id}/copy`, title === undefined ? {} : { title });
}

export async function deleteGroove(id: string): Promise<void> {
  await send("DELETE", `/grooves/${id}`);
}
